import { later, next } from '@ember/runloop';

import $ from 'jquery';

import type { DChartInterface } from 'webapp-types';

import AnnotationsBaseHandler from 'webapp/d-chart/annotations/base';

export default class AnnotationsGridHandler extends AnnotationsBaseHandler {
	override attachToChart(hChart: Highcharts.Chart): void {
		super.attachToChart(hChart);

		this.openViewAnnotationOnLoad();

		this.setupEventCatcherEl();
	}

	override update(): void {
		super.update();

		this.updateEventCatcherElSize();
	}

	protected getLabelPointForAnnotatablePoint(annotatablePointId: string): {
		x: number;
		y: number;
		xAxis: number;
	} {
		const yAxis = this.hChart.yAxis[0] as DChartInterface.AxisWithLayout,
			posX = this.annotatablePointsIds.indexOf(annotatablePointId),
			posY = yAxis.top + (yAxis.visible ? yAxis.height : yAxis.height * 0.8); // smaller gap for simple charts that have axis hidden

		// returning exact pixels for y and specific coordinate for x, because xAxis is defined.
		return {
			x: posX,
			y: posY,
			xAxis: 0,
		};
	}

	// ------- EVENT CATCHER ELEMENT ------

	private handleEventCatcherMoveEvent(e: JQuery.MouseMoveEvent<HTMLElement>): void {
		if (!this.canAnnotate) {
			return;
		}

		const hc = this.hChart;

		if (!hc) {
			return;
		}

		const eventWithChartData = hc.pointer.normalize(e.originalEvent!);

		let categoryIndex = hc.xAxis[0]?.toValue(eventWithChartData.chartX) || 0;
		categoryIndex = Math.round(categoryIndex);

		// find closest x pt
		const closestX = this.annotatablePointsIds[categoryIndex];

		if (typeof closestX === 'undefined') {
			return;
		}

		this.showAddButton(closestX);
	}

	private setupEventCatcherEl() {
		const hc = this.hChart;

		if (!hc) {
			return;
		}

		const eventCatcherEl = $('<div class="dbx-annotation-event-catcher"></div>');

		eventCatcherEl
			.on('mouseleave', () => {
				later(() => {
					this.hideAddButton();
				}, 100);
			})
			.on('mousemove', (e) => {
				this.handleEventCatcherMoveEvent(e);
			});

		eventCatcherEl.insertAfter(hc.renderer.box);

		this.eventCatcherEl = eventCatcherEl;
	}

	private updateEventCatcherElSize(): void {
		const hc = this.hChart;

		if (!hc) {
			return;
		}

		if (!this.eventCatcherEl) {
			return;
		}

		const xAxis = hc.xAxis[0] as DChartInterface.AxisWithLayout,
			axisSizeModifier = xAxis.visible ? 1.4 : 1.1,
			width = xAxis.width,
			height =
				this.renderer.dChart.sizeModifier *
				axisSizeModifier *
				AnnotationsGridHandler.HEIGHT,
			bottom = xAxis.bottom - height,
			left = xAxis.left;

		this.eventCatcherEl.css({ bottom, left, width, height });
	}

	private openViewAnnotationOnLoad(): void {
		next(() => {
			AnnotationsGridHandler.defaultAnnotationTimestamps.forEach((id) => {
				this.openViewAnnotations(
					String(id),
					document.body.querySelector('.dbx-annotation-label--view')!,
				);
			});
		});
	}

	// eslint-disable-next-line ember/classic-decorator-hooks
	override destroy(): void {
		super.destroy();

		if (this.eventCatcherEl) {
			this.eventCatcherEl.remove();
			this.eventCatcherEl = null;
		}
	}

	static HEIGHT = 1.2; // how many ems (fontsizes) to reserve for annotations under x axis
	static defaultAnnotationTimestamps: number[] = [];
}
